import { LoadingButton } from '@mui/lab'
import {
  Avatar,
  Box,
  Skeleton,
  Stack,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { FC, Fragment, useState } from 'react'
import { Icons, SkeletonText } from 'utoniq-ui'

import {
  Artist,
  ArtistCustomerConnectionFragment,
  Maybe,
  MembershipSubscriptionProduct,
  OrderBy,
} from '@/clients/utoniq-core/schema'
import {
  ArtistHeaderItemsLeft,
  ArtistHeaderItemsLeftProps,
  ArtistHeaderItemsRight,
  BaseLayout,
  Head,
} from '@/components/layouts'
import { TableScrollFetcher } from '@/components/organisms/functionals'
import {
  SearchForm,
  SearchFormValues,
} from '@/components/pages/ConsoleUsers/template/SearchForm'
import { ArtistThemeProvider, styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  user: ArtistHeaderItemsLeftProps['currentUser']
  artistId: string
  artist: Maybe<
    Pick<Artist, 'id' | 'paymentType' | 'hasArrangingDeliveryItems'>
  >
  loading: boolean
  refresh: boolean
  hasNextPage: boolean
  downloadingCsv: boolean
  haveSearchTermValue: boolean
  users: Maybe<ArtistCustomerConnectionFragment>
  sort: OrderBy
  membership: Pick<MembershipSubscriptionProduct, 'productId'>[] | undefined
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
  onSubmit: (values: SearchFormValues) => Promise<void>
  onDownloadCsv: () => Promise<void>
}

export const ConsoleUsersTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onSubmit,
  onDownloadCsv,
}) => {
  const { t } = useTranslation('consoleUsers')
  const { routerNames } = useRouter()
  const [onFocusInputSearch, setOnFocusInputSearch] = useState<boolean>(false)

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleUsers} />
      <BaseLayout
        margin
        header={{
          left: (
            <ArtistHeaderItemsLeft
              currentUser={state.user}
              paymentType={state.artist?.paymentType}
              hasArrangingDeliveryItems={
                state.artist?.hasArrangingDeliveryItems
              }
              membership={state.membership}
            />
          ),
          right: <ArtistHeaderItemsRight artistId={state.artistId} />,
        }}
      >
        {state.loading && !state.users?.edges.length ? (
          <Box sx={{ py: 3, mt: 2 }}>
            <SkeletonText variant="large" width="70%" />
          </Box>
        ) : (
          <Stack
            direction="row"
            mt={2}
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h3" sx={{ py: 3 }}>
              {t('usersManagement')}
            </Typography>
            {!(onFocusInputSearch || state.haveSearchTermValue) && (
              <DownloadCsvButtonCell
                loading={state.downloadingCsv}
                onClick={onDownloadCsv}
              />
            )}
          </Stack>
        )}
        {state.loading && !state.users?.edges.length ? (
          <SearchFormSkeleton />
        ) : (
          <SearchForm
            artistId={state.artistId}
            onSubmit={onSubmit}
            onFocusInputSearch={() => setOnFocusInputSearch(true)}
            onBlurInputSearch={() => setOnFocusInputSearch(false)}
            sort={state.sort}
          />
        )}
        <MemberListCell
          users={state.users}
          hasNextPage={state.hasNextPage}
          onFetchMore={onFetchMore}
          loading={state.loading || state.refresh}
        />
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

type MemberListCellProps = {
  loading: State['loading']
  hasNextPage: State['hasNextPage']
  onFetchMore: Action['onFetchMore']
  users: State['users']
}
const MemberListCell: Cell<MemberListCellProps> = ({
  loading,
  hasNextPage,
  users,
  onFetchMore,
}) => {
  const { t } = useTranslation('consoleUsers')
  const { routerPages, generatePath, router } = useRouter()

  if (!loading && !users?.edges.length) {
    return (
      <Typography
        variant="body1"
        align="center"
        sx={{ py: 6 }}
        color="text.secondary"
      >
        {t('usersEmpty')}
      </Typography>
    )
  }

  return (
    <CellFrame>
      <TableContainer>
        <Table aria-label="serial-number-table" sx={{ width: 'auto' }}>
          <TableHead>
            <TableRow>
              <TableCell width="100%" sx={{ border: 'none', py: 0, px: 0 }}>
                {loading && !users?.edges.length ? (
                  <SkeletonText variant="small" width="30%" />
                ) : (
                  <Typography variant="overline" color="text.hint">
                    {t('name', 'ユーザー名/電話番号')}
                  </Typography>
                )}
              </TableCell>
              <TableCell
                align="center"
                sx={{ border: 'none', minWidth: 70, px: 2, py: 0 }}
              >
                {loading && !users?.edges.length ? (
                  <SkeletonText variant="small" width="70%" />
                ) : (
                  <Typography variant="overline" color="text.hint">
                    {t('purchasesNumber', '購入回数')}
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ border: 'none', minWidth: 100, px: 2, py: 0 }}>
                {loading && !users?.edges.length ? (
                  <SkeletonText variant="small" width="70%" />
                ) : (
                  <Typography variant="overline" color="text.hint">
                    {t('lastPurchase', '最終購入日')}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableScrollFetcher
            loading={loading}
            hasNextPage={hasNextPage}
            onFetchMore={onFetchMore}
            component={TableRow}
            colSpan={3}
            nodes={users?.edges.map(({ node: user }) => (
              <Fragment key={user.id}>
                <TableCell
                  width="100%"
                  padding="none"
                  sx={{ py: 4, maxWidth: 100 }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    sx={{ cursor: 'pointer' }}
                    onClick={() =>
                      router.push(
                        generatePath(routerPages.ConsoleUserDetail, {
                          userId: user.userId,
                        })
                      )
                    }
                  >
                    <Avatar
                      src={user.user?.avatar?.smallUri ?? ''}
                      sx={{ width: 28, height: 28 }}
                    />
                    <Stack flex={1} sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} sx={{ minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          sx={styleSets.lineCamp(1)}
                        >
                          {user.user?.fullName ?? user.user?.displayName}
                        </Typography>
                        {user.patron && (
                          <Icons.Group
                            fontSize="small"
                            sx={{ color: 'secondary.main' }}
                          />
                        )}
                      </Stack>
                      <Typography variant="overline" color="text.secondary">
                        {user.user?.formattedPhoneNumber}
                      </Typography>
                    </Stack>
                  </Stack>
                </TableCell>
                <TableCell
                  align="center"
                  padding="none"
                  sx={{ py: 4, minWidth: 70, px: 2 }}
                >
                  <Typography variant="overline" color="text.secondary">
                    {user.formattedPurchaseCount}
                  </Typography>
                </TableCell>
                <TableCell padding="none" sx={{ py: 4, minWidth: 100, px: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    {user.formattedLastPurchasedAt}
                  </Typography>
                </TableCell>
              </Fragment>
            ))}
            loadingComponent={
              <>
                {Array.from(Array(3).keys()).map(key => (
                  <TableRow key={key}>
                    <TableCell padding="none" sx={{ py: 4, maxWidth: 100 }}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Skeleton variant="circular" width={28} height={28} />
                        <Stack flex={1} sx={{ minWidth: 0 }}>
                          <SkeletonText variant="small" width="50%" />
                          <SkeletonText variant="small" width="80%" />
                        </Stack>
                      </Stack>
                    </TableCell>
                    <TableCell
                      padding="none"
                      sx={{ py: 4, minWidth: 70, px: 2 }}
                    >
                      <Stack flex={1} spacing={3} sx={{ px: 5 }}>
                        <SkeletonText variant="caption" width={60} />
                      </Stack>
                    </TableCell>
                    <TableCell
                      padding="none"
                      sx={{ py: 4, minWidth: 100, px: 2 }}
                    >
                      <Stack flex={1} spacing={3} sx={{ px: 5 }}>
                        <SkeletonText variant="caption" width={60} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </>
            }
          />
        </Table>
      </TableContainer>
    </CellFrame>
  )
}

const SearchFormSkeleton: Cell = () => {
  return (
    <CellFrame>
      <Box sx={{ borderRadius: '22px', bgcolor: 'grey.50', px: 6, py: 3 }}>
        <SkeletonText variant="h3" width="60%" />
      </Box>
      <Stack sx={{ mt: 2, py: 2 }} alignItems="flex-end">
        <SkeletonText variant="large" width="30%" />
      </Stack>
    </CellFrame>
  )
}

type DownloadCsvButtonCellProps = {
  loading?: boolean
  onClick: () => void
}
const DownloadCsvButtonCell: FC<DownloadCsvButtonCellProps> = ({
  loading,
  onClick,
}) => {
  const { t } = useTranslation('consoleUsers')
  return (
    <CellFrame>
      <LoadingButton
        size="small"
        loadingPosition="start"
        variant="outlined"
        sx={{
          '.MuiLoadingButton-loadingIndicator': {
            left: 'auto',
            color: 'primary.main',
          },
          borderRadius: 1,
          height: 28,
          borderColor: 'primary.main',
          color: 'primary.main',
          '&.MuiLoadingButton-loading': {
            borderColor: 'grey.100',
          },
        }}
        onClick={onClick}
        loading={loading}
      >
        <Typography variant="subtitle1" color="inherit" pt="2px">
          {loading
            ? t('downloadNow', 'ダウンロード中')
            : t('exportCsv', 'CSVエクスポート')}
        </Typography>
      </LoadingButton>
    </CellFrame>
  )
}
